import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';
import {
  CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import { Input, Checkbox } from '../../common/Input';
import { useDropdownValues } from '../../hooks/useDropdownFetch';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
import { fetchDropdownTypesEnum } from "../../api/fetch";
import { transformEnumData } from "../../utils/textUtils";

// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import { Select } from "../../common/Select";

function GovPremiumFormulas({ user, isHideSideMenu }) {
  // Use the hook for message management
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  var headers = [
    "ID",
    "Priority Level",
    "Description",
    "Selected Type",
    "Selected Value",
    "Has Pagibig",
    "Has SSS",
    "Has Philhealth",
    "Status",
    "Actions"
  ]

  var accessors = [
    "id",
    "priority",
    "description",
    "dropdownTypeName",
    "dropdownValueName",
    "hasPagibig",
    "hasSSS",
    "hasPhilhealth",
    "isActive",
    "actions"
  ]

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  // Enum states for dynamic filtering
  const [dropdownTypeOptions, setDropdownTypeOptions] = useState([]);
  const [enumsLoading, setEnumsLoading] = useState(false);

  // Dynamic dropdown data
  const {
    dropdownValueOptions,
    dropdownValueLoading,
    fetchDropdownValues,
    resetDropdownValues,
    clearCacheForType
  } = useDropdownValues();

  // Form state
  const [form, setForm] = useState({
    description: "",
    priority: "1",
    dropdownType: "",
    dropdownValue: "",
    hasPagibig: false,
    hasSSS: false,
    hasPhilhealth: false,
    isActive: true,
  });

  const resetLocalVariables = () => {
    setForm({
      description: "",
      priority: "1",
      dropdownType: "",
      dropdownValue: "",
      hasPagibig: false,
      hasSSS: false,
      hasPhilhealth: false,
      isActive: true,
    });
    setSelectedData(null);
    resetDropdownValues();
  }

  // Retrieve
  const [filterData, setFilterData] = useState([]);
  // Add
  const [showAddForm, setShowAddForm] = useState(false);
  // Update
  const [showEditForm, setShowEditForm] = useState(false);

  const apiUrl = domain + "/api/GovPremiumFormula";

  // Fetch enums from API
  const fetchEnums = useCallback(async () => {
    try {
      setEnumsLoading(true);
      const dropdownRes = await fetchDropdownTypesEnum();

      setDropdownTypeOptions(transformEnumData(dropdownRes));
    } catch (error) {
      console.error('Error fetching enums:', error);
    } finally {
      setEnumsLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      console.log(response.data);
      setData(response.data);
      setFilterData(response.data);
      console.log("GovPremiumFormulas: ", response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchEnums();
    fetchData();
  }, [fetchEnums, fetchData]);

  // Fetch dropdown values when dropdown type changes
  useEffect(() => {
    if (form.dropdownType) {
      fetchDropdownValues(form.dropdownType);
    } else {
      resetDropdownValues();
    }
  }, [form.dropdownType, fetchDropdownValues, resetDropdownValues]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    var res = filterData.filter((f) =>
      f?.description?.toString().toLowerCase().includes(lowercaseValue) ||
      f?.priority?.toString().includes(lowercaseValue) ||
      f?.dropdownTypeName?.toString().toLowerCase().includes(lowercaseValue) ||
      f?.dropdownValueName?.toString().toLowerCase().includes(lowercaseValue)
    );
    setData(res);
  };

  const handleAddSubmit = async (event) => {
    event.preventDefault();

    // Validate form
    if (!form.description) {
      showGeneralMessage("Please fill in description field", false);
      return;
    }

    // Prepare data for API
    const apiData = {
      description: form.description.trim(),
      priority: parseInt(form.priority) || null,
      dropdownType: form.dropdownType ? parseInt(form.dropdownType) : null,
      dropdownValue: form.dropdownValue ? parseInt(form.dropdownValue) : null,
      hasPagibig: form.hasPagibig !== undefined ? form.hasPagibig : false,
      hasSSS: form.hasSSS !== undefined ? form.hasSSS : false,
      hasPhilhealth: form.hasPhilhealth !== undefined ? form.hasPhilhealth : false,
      isActive: form.isActive !== undefined ? form.isActive : true,
    };

    console.log('Add Submitting data:', apiData);

    console.log('JSON to be sent:', JSON.stringify(apiData, null, 2));

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchData();

      showGeneralMessage("Government premium formula created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create government premium formula";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdateSubmit = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) {
      showGeneralMessage("No record selected for update", false);
      return;
    }

    // Validate form
    if (!form.description) {
      showGeneralMessage("Please fill in description field", false);
      return;
    }

    // Prepare data for API
    const apiData = {
      description: form.description.trim(),
      priority: parseInt(form.priority) || null,
      dropdownType: form.dropdownType ? parseInt(form.dropdownType) : null,
      dropdownValue: form.dropdownValue ? parseInt(form.dropdownValue) : null,
      hasPagibig: form.hasPagibig !== undefined ? form.hasPagibig : false,
      hasSSS: form.hasSSS !== undefined ? form.hasSSS : false,
      hasPhilhealth: form.hasPhilhealth !== undefined ? form.hasPhilhealth : false,
      isActive: form.isActive !== undefined ? form.isActive : true,
    };

    console.log('Updating data:', apiData);
    console.log('JSON to be sent:', JSON.stringify(apiData, null, 2));

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setShowEditForm(false);
      await fetchData();

      showGeneralMessage("Government premium formula updated successfully!", true);
      resetLocalVariables();
    } catch (error) {
      console.error('Error updating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update government premium formula";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);
    setForm({
      description: data.description || "",
      priority: data.priority?.toString() || null,
      dropdownType: data.dropdownType?.toString() || "",
      dropdownValue: data.dropdownValue?.toString() || "",
      hasPagibig: data.hasPagibig !== undefined ? data.hasPagibig : false,
      hasSSS: data.hasSSS !== undefined ? data.hasSSS : false,
      hasPhilhealth: data.hasPhilhealth !== undefined ? data.hasPhilhealth : false,
      isActive: data.isActive !== undefined ? data.isActive : true,
    });
    openEditHandleClick();
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const openEditHandleClick = () => {
    setShowEditForm(true);
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Dev Mode >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Government Premium"}</span>
              </div>
              <h3>Government Premium Formulas</h3>
            </div>
            <div className='p-4 space-y-4'>
              <SearchBar handleFilter={handleFilter} />

              <Button
                icon={<AddIcon />}
                name={"Restrict Government Premium"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={openAddHandleClick}
              />

              <CreateTable
                tableName={"GovernmentPremiumFormula"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Form */}
      <AddEditForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleAddSubmit}
        modalName={"Restrict Government Premium"}
        mode="add"
        dropdownTypeOptions={dropdownTypeOptions}
        dropdownValueOptions={dropdownValueOptions}
        dropdownValueLoading={dropdownValueLoading}
        enumsLoading={enumsLoading}
        fetchDropdownValues={fetchDropdownValues}
        resetDropdownValues={resetDropdownValues}
        clearCacheForType={clearCacheForType}
      />

      {/* Edit Form */}
      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdateSubmit}
        modalName={"Update Restricted Government Premium"}
        mode="edit"
        dropdownTypeOptions={dropdownTypeOptions}
        dropdownValueOptions={dropdownValueOptions}
        dropdownValueLoading={dropdownValueLoading}
        enumsLoading={enumsLoading}
        fetchDropdownValues={fetchDropdownValues}
        resetDropdownValues={resetDropdownValues}
        clearCacheForType={clearCacheForType}
      />

      {/* Use the reusable GeneralMessage component */}
      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading || enumsLoading || dropdownValueLoading} />

    </div>
  )
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add",
  dropdownTypeOptions,
  dropdownValueOptions,
  dropdownValueLoading,
  enumsLoading,
  fetchDropdownValues,
  resetDropdownValues,
  clearCacheForType
}) {

  const submit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  const handleClose = () => {
    onClose();
  };

  const handleInputChange = (field, value) => {
    if (field === 'dropdownType' && form.dropdownType !== value) {
      if (form.dropdownType) {
        clearCacheForType(form.dropdownType);
      }
      setForm(prev => ({
        ...prev,
        [field]: value,
        dropdownValue: ""
      }));

      if (value) {
        fetchDropdownValues(value);
      } else {
        resetDropdownValues();
      }
    } else {
      setForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const getDropdownLabel = () => {
    const dropdownOption = dropdownTypeOptions.find(opt => opt.value.toString() === form.dropdownType);
    return dropdownOption ? `${dropdownOption.label}` : 'Value';
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
      >
        <div className="bg-gray-50 w-full md:w-[90%] lg:w-[50%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">

          {/* Header */}
          <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>

            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-grow space-y-4">

            {/* Priority Section */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Priority Rules</h3>
                <div className="text-sm text-gray-500 italic">
                  * Lower priority numbers are selected first. 1 = highest priority
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-3 pt-3">
                <Input
                  label="Priority Level"
                  value={form.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  placeholder="Enter priority number (e.g. 1, 2)"
                  type="number"
                  min="1"
                  max="999"
                  isRequired={true}
                />
              </div>
            </div>

            {/* Dynamic Filtering - Optional */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Filtering (Optional)</h3>
                <div className="text-sm text-gray-500 italic">
                  * Leave empty to apply to all
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-3 pt-3 space-y-3">
                <Select
                  label="Select Type"
                  value={form.dropdownType}
                  onChange={(e) => handleInputChange('dropdownType', e.target.value)}
                  options={dropdownTypeOptions}
                  placeholder={enumsLoading ? "Loading types..." : "Select type (optional)"}
                  isRequired={false}
                  allowSelectAll={true}
                  isDisabled={enumsLoading}
                />

                {form.dropdownType && (
                  <Select
                    label={getDropdownLabel()}
                    value={form.dropdownValue}
                    onChange={(e) => handleInputChange('dropdownValue', e.target.value)}
                    options={dropdownValueOptions}
                    placeholder={dropdownValueLoading ? "Loading options..." : `Select ${getDropdownLabel()} (optional)`}
                    isRequired={false}
                    isDisabled={dropdownValueLoading}
                    allowSelectAll={true}
                    className={"!mt-0"}
                    parentClassName={"!mt-0"}
                  />
                )}
              </div>
            </div>

            {/* Premium Information */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Premium Information</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 pb-3 pt-3">
                <Input
                  label="Description:"
                  value={form.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter description (e.g., 'Standard Premium', 'Special Rate 2024')"
                  isRequired={true}
                />

                {/* Government Benefits Checkboxes */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700">Government Benefits:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Checkbox
                      label="Has Pagibig"
                      checked={form.hasPagibig !== undefined ? form.hasPagibig : false}
                      onChange={(e) => handleInputChange('hasPagibig', e.target.checked)}
                      tooltip="Check if this premium includes Pagibig benefits"
                    />
                    
                    <Checkbox
                      label="Has SSS"
                      checked={form.hasSSS !== undefined ? form.hasSSS : false}
                      onChange={(e) => handleInputChange('hasSSS', e.target.checked)}
                      tooltip="Check if this premium includes SSS benefits"
                    />
                    
                    <Checkbox
                      label="Has Philhealth"
                      checked={form.hasPhilhealth !== undefined ? form.hasPhilhealth : false}
                      onChange={(e) => handleInputChange('hasPhilhealth', e.target.checked)}
                      tooltip="Check if this premium includes Philhealth benefits"
                    />
                  </div>
                </div>

                <Checkbox
                  label="Active"
                  checked={form.isActive !== undefined ? form.isActive : true}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white shadow-sm">
            <button
              onClick={handleClose}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={submit}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
              disabled={enumsLoading || dropdownValueLoading}
            >
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

export default GovPremiumFormulas